import { useRef } from 'react';

import { STEP_NAMES } from '../constants/steps';
import type { StepKey, StepRefs, StepRefsHook } from '../data-layer/types';

/**
 * Custom hook for managing step references
 */
export function useStepRefs(): StepRefsHook {
  const stepRefs: StepRefs = {
    [STEP_NAMES.GENERAL]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.CONNECTION]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.CREATE_ACCOUNT]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.ADD_ACCESS]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.UPDATE_ACCOUNT]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.ENABLE_ACCOUNT]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.DISABLE_ACCOUNT]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.REMOVE_ACCOUNT]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.REMOVE_ACCESS]: useRef<HTMLDivElement>(null),
    [STEP_NAMES.CONFIRMATION]: useRef<HTMLDivElement>(null),
  };

  /**
   * Smooth scroll to a specific step section
   */
  const scrollToStep = (stepName: StepKey | string) => {
    // Handle both StepKey and direct step name
    const targetStepName =
      typeof stepName === 'string' ? stepName : STEP_NAMES[stepName];

    // Skip scrolling for entitlement steps since they don't have individual refs
    if (
      targetStepName.startsWith('AddAccess_') ||
      targetStepName.startsWith('RemoveAccess_')
    ) {
      return; // Just skip scrolling, the state updates will handle the UI
    }

    const ref = stepRefs[targetStepName as keyof StepRefs];

    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return {
    stepRefs,
    scrollToStep,
  };
}

// Re-export for backward compatibility
export { STEP_NAMES } from '../constants/steps';
